import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { ArrowRight, MapPin, CalendarDays, Receipt, ShieldCheck, Building2, User, HeartHandshake } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { trackCtaClick } from "@/lib/analytics";

type Item = { title: string; body: string };
type Faq = { q: string; a: string };

export default function TpeParisCrmPage() {
  const { t } = useTranslation();
  const profiles = t("tpeParis.profiles", { returnObjects: true }) as Item[];
  const faq = t("tpeParis.faq", { returnObjects: true }) as Faq[];
  const why = [MapPin, CalendarDays, Receipt, ShieldCheck];
  const profileIcons = [Building2, User, HeartHandshake];

  const SignupCta = ({ where }: { where: string }) => (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button asChild size="lg" className="h-12 px-6">
        <Link to="/auth?mode=signup" onClick={() => trackCtaClick?.(`tpe-paris-${where}`, "/auth?mode=signup")}>
          {t("tpeParis.ctaPrimary")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12 px-6">
        <Link to="/pricing">{t("tpeParis.ctaSecondary")}</Link>
      </Button>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background text-foreground overflow-x-hidden">
      <PublicHeader />

      <section className="pt-32 pb-16 md:pt-44 md:pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm font-sans">
            <MapPin className="h-3.5 w-3.5 mr-1.5" />
            {t("tpeParis.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold leading-tight tracking-tight">
            {t("tpeParis.h1")} <span className="text-primary">{t("tpeParis.h1Accent")}</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            {t("tpeParis.subtitle")}
          </p>
          <div className="mt-8"><SignupCta where="hero" /></div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">{t("tpeParis.whyTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {why.map((Icon, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-display font-semibold text-lg mb-2">{t(`tpeParis.why${i + 1}Title`)}</h3>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">{t(`tpeParis.why${i + 1}Body`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">{t("tpeParis.profilesTitle")}</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {profiles.map((p, i) => {
              const Icon = profileIcons[i] ?? User;
              return (
                <Card key={p.title} className="h-full">
                  <CardContent className="p-6">
                    <Icon className="h-6 w-6 text-primary mb-3" />
                    <h3 className="font-display font-semibold text-lg mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">{p.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">{t("tpeParis.stepsTitle")}</h2>
          <ol className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-display font-bold mb-3">{n}</span>
                    <h3 className="font-display font-semibold text-lg mb-2">{t(`tpeParis.step${n}Title`)}</h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">{t(`tpeParis.step${n}Body`)}</p>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ol>
          <div className="mt-8"><SignupCta where="steps" /></div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">{t("tpeParis.faqTitle")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`tpe-faq-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold min-h-[56px]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground font-sans leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t("tpeParis.finalTitle")}</h2>
          <p className="text-muted-foreground font-sans mb-8">{t("tpeParis.finalBody")}</p>
          <SignupCta where="final" />
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
